'use client';

/**
 * AddStopByPlace — "type a place, add it as a stop" for the explorer's trip
 * panel, and (with `insertAt`) for the inline "add a stop here" box between
 * two ladder rows.
 *
 * Deliberately a thin wrapper over LocationAutocomplete rather than its own
 * search box: both surfaces then share one /api/geocode implementation, one set
 * of error strings, and one dropdown whose click semantics have been verified.
 * A second hand-rolled place search is exactly how the two planners diverged in
 * the first place.
 *
 * LocationAutocomplete is a "hold one resolved location" control, but adding a
 * stop is a repeatable action — the box has to come back empty for the next
 * one. Remounting via `key` is what clears it: the component seeds its text
 * from `value?.label`, so simply setting the value back to null does not change
 * that dependency and would leave the previous place sitting in the field.
 */

import React, { useState } from 'react';
import LocationAutocomplete, { ResolvedLocation } from './LocationAutocomplete';
import type { Anchor } from '../utils/placeSuggest';
import type { PlacementInfo } from '../utils/tripInsert';

interface AddStopByPlaceProps {
  /** `insertAt` echoes the prop back, so one handler can serve every box. */
  onAdd: (place: ResolvedLocation, insertAt?: number) => void;
  placeholder?: string;
  /** Visible label. Stays the same as the trip grows — the placeholder carries
   *  the context ("Where to?"), so nothing shifts under the cursor. */
  label?: string;
  /** "Use my location" — handy for the start, noise for later stops. */
  allowMyLocation?: boolean;
  /** Marker inside the field (the start/next-stop pin). */
  leadingIcon?: React.ReactNode;
  /** A secondary action at the right edge of the field. */
  trailingAction?: React.ReactNode;
  hideLabel?: boolean;
  /** 'large' — 52px field with a search icon; 'medium' — the same at 44px
   *  (see LocationAutocomplete). */
  size?: 'default' | 'medium' | 'large';
  /** Put the stop at this index instead of letting the planner place it. */
  insertAt?: number;
  /** Points the trip already covers — results near them rank first. */
  near?: Anchor[];
  autoFocus?: boolean;
  onEscape?: () => void;
  ariaLabel?: string;
  /** The drawn road route: rows say how far off it they are. */
  route?: Anchor[] | null;
  /** Where a pick would land and what it adds, for each row's chip. */
  placement?: (p: { lat: number; lng: number }) => PlacementInfo | null;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function AddStopByPlace({
  onAdd,
  placeholder,
  label,
  allowMyLocation = true,
  leadingIcon,
  trailingAction,
  hideLabel,
  size,
  insertAt,
  near,
  autoFocus,
  onEscape,
  ariaLabel,
  route,
  placement,
  disabled,
  onOpenChange,
}: AddStopByPlaceProps) {
  const [resetKey, setResetKey] = useState(0);

  return (
    <LocationAutocomplete
      key={resetKey}
      label={label}
      placeholder={placeholder ?? 'Town, park or campsite'}
      value={null}
      onChange={(loc) => {
        if (!loc) return;
        onAdd(loc, insertAt);
        setResetKey((k) => k + 1);
      }}
      allowMyLocation={allowMyLocation}
      leadingIcon={leadingIcon}
      trailingAction={trailingAction}
      hideLabel={hideLabel}
      size={size}
      near={near}
      // A remount after an add keeps focus in the box for the next stop.
      autoFocus={autoFocus || resetKey > 0}
      onEscape={onEscape}
      ariaLabel={ariaLabel}
      route={route}
      placement={placement}
      disabled={disabled}
      onOpenChange={onOpenChange}
    />
  );
}
